import { JoiEvent, LabelMaps, Ranking, Venue } from "../../globals/store/events.data"
import { OnsiteActivity, OnsiteSchedule, OnsiteSingleTimeline, OnsiteTimelineBrick, ProgramNode, ProgramNodeDateMap, ProgramNodeMap } from "../../globals/store/onsite.data"
import { DateSchedule, Program } from "../../globals/store/programs.data"
import { ShareOSSInfo } from "../../globals/store/share.data"
import { Performer } from "../../globals/store/performers.data"
import { Sponsor } from "../../globals/store/sponsors.data"
import { getAllTagPanelData, TagPanelData } from "../../globals/store/tags.data"
import { BrandingData, getBrandingData } from "../../globals/store/publish.data"
import { WFWorker, WorkforceGroup } from "../../globals/store/workforce.data"
import { Exhibitor, ExhibitorStand } from "../../globals/store/exhibitors.data"
import { JoiResource } from "../../globals/store/resources.data"
import { JoiAccount } from "../../globals/store/accounts.data"
import { Attendee } from "../../globals/store/attendees.data"
import { Asset } from "../../globals/store/assets.data"
import { flatten, getDefaultTimeZoneDetails, getSet, jsonCopyObject, makeMapFromListOfObjectsAndKey, str2Date } from "../../globals/helpers/helpers"
import { sortOnsiteTimelineBrick } from "../../globals/store/reselectors/onsite.reselector.filtering"
import { getValidSortedStagingRequests } from "../../globals/store/share.brief.functions"
import { getOnsiteSchedules } from "../../globals/share/onsite.getoss"
import { filterOnsiteTimelineFilters, filterPerformersByExcludedOSSFieldsAndFilters, filterShareableByExcludedOSSFields, filterSponsorsByExcludedOSSFieldsAndFilters, filterTimeline, filterTimelineByFields } from "../../globals/share/onsite.filtering"


export const populateTimelineData = (programNodeDateMap: ProgramNodeMap, multiTimeline: DateSchedule, performers: { [pId: string]: Performer }, eventProgramOrder: string[]) => {


  programNodeDateMap.forEach(programNode => {
    if (programNode.subschedules) { // program schedule with activity subschedules
      const schedule = { ...programNode.schedule, sessions: [...programNode.schedule.sessions.filter(sess => sess.type !== 'break')] }
      // console.log(programNode.day, { programNode, schedule })
      if (programNode.schedule.type === 'program') {
        programNode.subschedules.forEach(subSchedule => schedule.sessions = schedule.sessions.concat(subSchedule.sessions))
      } else {
        programNode.subschedules.forEach(subSchedule => subSchedule.sessions.forEach(activity => activity.scheduleId = schedule.id )) // <== don't want to add a standalone's activities into the main schedule's sessions. HOWEVER, do want those activities to know about the main "schedule" (the standalone schedule)
      }
      schedule.sessions.sort(sortOnsiteTimelineBrick)
      schedule.sessions.forEach(sess => { 
        sess.scheduleId = schedule.scheduleId
        sess.stagingRequests = getValidSortedStagingRequests(sess.stagingRequests || [], performers)
      })
      multiTimeline.programs.push(schedule)
    } else {
      multiTimeline.programs.push(programNode.schedule)
    }
  })

  multiTimeline.programs.sort((a, b) => {
    const aIndex = (eventProgramOrder || []).indexOf(a.scheduleId)
    const bIndex = (eventProgramOrder || []).indexOf(b.scheduleId)
  
    const aInList = aIndex !== -1
    const bInList = bIndex !== -1
  
    if (aInList && bInList) {
      return aIndex - bIndex
    }
  
    if (aInList) return -1
    if (bInList) return 1
  
    return a.order - b.order
  })

}

export const initializeFullMultiTimeline = (programNodesByDate: ProgramNodeDateMap, performers: { [pId: string]: Performer }, eventProgramOrder: string[]): DateSchedule[] => {

  if (!programNodesByDate) {
    return []
  }
  const dates = Object.keys(programNodesByDate)
  // console.log({ dates: jsonCopyObject(dates), programNodesByDate })
  // dates.forEach(date => console.log(date, programNodesByDate[date]))

  const multiTimelines: DateSchedule[] = dates.map(dateId => ({id: dateId, date: str2Date(dateId), programs: []}))
  // console.log({ dates, multiTimelines, multiTimelinesCopied: jsonCopyObject(multiTimelines) })
  dates.forEach((dateId, index) => {
    // console.log(dateId)
    populateTimelineData(programNodesByDate[dateId], multiTimelines[index], performers, eventProgramOrder)
  })
  return multiTimelines
}

export interface FullSharedFilteredOSSTimelineAndShareableAndPerformersData {
  timeline: OnsiteSingleTimeline[]
  performers: { [pId: string]: Performer }
  sponsors: { [pId: string]: Sponsor }
  sponsorTypes: { [tId:string]: Ranking }
  venues: { [vId: string]: Venue }
  info: ShareOSSInfo
  showActivityStatus: boolean
  hideJoi?: boolean
  labelMaps?: LabelMaps
  tagMap: { [vId:string]: TagPanelData }
  brandingData: BrandingData
  performerRoles: { [pId: string]: Ranking }
}

export const getFullSharedFilteredOSSTimelineAndShareableAndPerformers = (
  activities: OnsiteActivity[] = [], 
  schedules: OnsiteSchedule[],
  allPrograms: Program[], 
  info: ShareOSSInfo, 
  event: JoiEvent,
  performers: { [pId: string]: Performer } = {}, 
  labelMaps: LabelMaps, 
  workforceGroupsById: { [id:string]: WorkforceGroup }, 
  workersByEmail: { [key:string]: WFWorker },
  sponsors: { [pId: string]: Sponsor } = {},
  sponsorTypes: { [tId:string]: Ranking },
  exhibitors: { [eId:string]: Exhibitor } = {},
  stands: { [sId:string]: ExhibitorStand },
  assets: { [id: string]: Asset },
  resources: { [rId: string]: JoiResource },
  account: Partial<JoiAccount>,
  eventProgramOrder: string[],
  attendees: { [aId: string]: Attendee },
): FullSharedFilteredOSSTimelineAndShareableAndPerformersData => {
  if (!schedules || !allPrograms || !info || !event) {
    console.log("SOMETHING MISSING!")
    return null
  }
  info.eventTitle = event? event.title : ''
  info.eventId = event ? event.eventId : ''

  const { excludedFieldIds } = info
  const programs = jsonCopyObject(allPrograms)
  programs.filter(p => !p.hasOwnProperty('setups')).forEach(p => p.setups = {}) 
  if (excludedFieldIds) {
    if (excludedFieldIds.includes('setups')) programs.forEach(p => p.setups = {})
    if (excludedFieldIds.includes('access')) programs.forEach(p => Object.values(p.setups).forEach(s => {
      s.access = null 
      s.accessDay = null
    }))
    if (excludedFieldIds.includes('clear')) programs.forEach(p => Object.values(p.setups).forEach(s => {
      s.clear = null 
      s.clearDay = null
    }))
  }

  const { programNodesByDate } = getOnsiteSchedules(activities, schedules, programs, true)
  const multiTimelines = initializeFullMultiTimeline(programNodesByDate, performers, event.programOrder)
  
  const filters = info.filters || { count: 0 }
  const showActivityStatus = !excludedFieldIds || !excludedFieldIds.includes('activityStatus') // NOTE:  showActivityStatus will only filter through here in the app

  filterOnsiteTimelineFilters(filters,  multiTimelines, performers, labelMaps, workforceGroupsById, workersByEmail, sponsors, exhibitors, event?.exhibitorStands || {}, resources, attendees)
  // console.log({ filters })

  // console.log("getFullSharedFilteredOSSTimelineAndShareableAndPerformers")
  const filteredMultiTimelines = filters && !filters.count ? multiTimelines : filterTimeline(multiTimelines, filters, true, exhibitors, 'oss', stands, makeMapFromListOfObjectsAndKey(programs, 'id'))
  // console.log({ filteredMultiTimelines })

  const allProgramNodes = flatten(Object.values(programNodesByDate).map(programNodesMap => Array.from(programNodesMap.values())))
  sortProgramNodesList(allProgramNodes, eventProgramOrder)
  const fullScheduleOrder = allProgramNodes.map(p => p.schedule.scheduleId)
  const timeline = convertFullMultiTimelineToSingleTimeline(filteredMultiTimelines, assets, fullScheduleOrder)
  // console.log({ timeline })
  
  const filteredTimelineByFields = excludedFieldIds && excludedFieldIds.length ? filterTimelineByFields(excludedFieldIds, timeline) : timeline
  // console.log({ filteredTimelineByFields })

  const filteredPerformers = filterPerformersByExcludedOSSFieldsAndFilters(info, performers, filters)
  // console.log({ filteredPerformers })
  const filteredSponsors = filterSponsorsByExcludedOSSFieldsAndFilters(info, sponsors, filters)

  const filteredInfo = filterShareableByExcludedOSSFields(info)
  // console.log({ filteredInfo })

  const tagMap = getAllTagPanelData(programs, activities, workersByEmail, performers, sponsors, exhibitors, resources, attendees, false)
  const allSessionAndActivityIds = getSet(flatten(filteredTimelineByFields.map(t => t.bricks)), 'id')
  Object.keys(tagMap).filter(id => !allSessionAndActivityIds.has(id)).forEach(id => delete tagMap[id])

  return { 
    timeline: filteredTimelineByFields, 
    performers: filteredPerformers,
    info: filteredInfo, 
    showActivityStatus,
    sponsors: filteredSponsors,
    sponsorTypes,
    venues: event ? event.venues : {},
    tagMap,
    brandingData: getBrandingData({ acc: account, event, brandingSelectData: { type: 'forms', brandFormType: 'schedules' } }),
    performerRoles: excludedFieldIds.includes("performerRoles") ? null : makeMapFromListOfObjectsAndKey(event?.performerRoles || [], 'id')
  }
}

export const convertFullMultiTimelineToSingleTimeline = (multiTimelines: DateSchedule[], assets: { [id: string]: Asset }, fullScheduleOrder: string[]) =>
  multiTimelines.map(t => convertMultiTimelineToSingleTimeline(t, assets, fullScheduleOrder))



export const convertMultiTimelineToSingleTimeline = (multiTimeline: DateSchedule, assets: { [id: string]: Asset }, fullScheduleOrder: string[]) => {
  let bricks: OnsiteTimelineBrick[] = [];
  multiTimeline.programs.forEach(p => {
    bricks = bricks.concat(p.sessions.map(sess => {
      const brick: OnsiteTimelineBrick = {
        ...sess,
        program_title: p.title,
        program_location: p.location,
        venueId: p.venueId,
        program_scheduleId: p.scheduleId,
        program_timeZoneId: p.timeZoneId || getDefaultTimeZoneDetails().timeZoneId,
        program_type: p.type,
        isStandalone: p.order > 99,
        presentationAssets: sess.type === 'session' ? (sess.presentationAssetIds || []).map(id => assets[id]).filter(a => a) : [],
        otherAssets: (sess.assetIds || []).map(id => assets[id]).filter(a => a),
      }
      return brick
    }))
    bricks.sort((a,b) => sortOnsiteTimelineBrick(a, b, fullScheduleOrder))
  })
  const singleTimeline = { date: multiTimeline.date, bricks: bricks, dateId: multiTimeline.id }

  return singleTimeline
}

export const sortProgramNodesList = (programNodesList: ProgramNode[], programOrder: string[]): ProgramNode[] => {
  programNodesList.sort((n1, n2) => {
    const p1Index = programOrder.findIndex(id => id === n1.schedule.scheduleId)
    const p2Index = programOrder.findIndex(id => id === n2.schedule.scheduleId)
    // console.log({ p1Index, p2Index, n1: n1.schedule.title, n2: n2.schedule.title })
    if (p1Index > -1 && p2Index > -1) return p1Index - p2Index
    else if (p1Index > -1) return -1
    else if (p2Index > -1) return 1
    else return n1.schedule.order - n2.schedule.order // standalones go by original "order"
  })
  return programNodesList
}